import React, { memo, useState, useMemo, useRef } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Star,
  Sparkles,
  Zap,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Moon,
  MessageSquare,
  Gift,
  HelpCircle,
  ExternalLink,
  ShieldAlert,
  Flame,
  Clock
} from 'lucide-react';

export interface ReviewBoosterNodeData {
  googleReviewUrl?: string;
  delayHours?: number; // 1, 2, 6, 24
  negativeFeedbackFirewall?: boolean; // default true
  minRatingForGoogleLink?: number; // default 4
  includeIncentive?: boolean;
  incentiveText?: string;
  quietHours?: boolean; // default true (10 PM - 8 AM)
  customGuidance?: string;
  isEnabled?: boolean;
  [key: string]: unknown;
}

export const ReviewBoosterNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as ReviewBoosterNodeData;

  const isEnabled = nodeData.isEnabled !== false;
  const googleReviewUrl = nodeData.googleReviewUrl || '';
  const delayHours = nodeData.delayHours ?? 2;
  const negativeFeedbackFirewall = nodeData.negativeFeedbackFirewall !== false;
  const minRatingForGoogleLink = nodeData.minRatingForGoogleLink ?? 4;
  const includeIncentive = nodeData.includeIncentive === true;
  const incentiveText = nodeData.incentiveText ?? 'Get 5% discount on your next visit!';
  const quietHours = nodeData.quietHours !== false;
  const customGuidance = nodeData.customGuidance ?? '';

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const persistNodeDataDirectly = (allUpdates: Partial<ReviewBoosterNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          isEnabled,
          googleReviewUrl,
          delayHours,
          negativeFeedbackFirewall,
          minRatingForGoogleLink,
          includeIncentive,
          incentiveText,
          quietHours,
          customGuidance,
          ...allUpdates,
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'REVIEW_BOOSTER_NODE',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.error('[ReviewBoosterNode] Persist error:', err);
      }
    }, 400);
  };

  const handleFieldChange = (field: keyof ReviewBoosterNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly({ [field]: value });
  };

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[420px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#181105]/98 via-[#0F0B03]/98 to-[#080501]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-amber-400 ring-2 ring-amber-400/50 shadow-[0_0_35px_rgba(245,158,11,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-amber-500/40 hover:border-amber-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(245,158,11,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Reviews in Portal ── */}
      <div className="absolute -top-11 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none group-hover:pointer-events-auto z-50">
        <button
          onClick={(e) => {
            e.stopPropagation();
            openPortalData('review_booster', id);
          }}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-950 via-slate-900 to-amber-900 border-2 border-amber-400 text-amber-200 text-[11px] font-mono font-bold tracking-wider shadow-[0_0_25px_rgba(245,158,11,0.8)] hover:scale-105 hover:text-white transition-all backdrop-blur-md cursor-pointer whitespace-nowrap active:scale-95"
          title="Open Reviews & Google Maps Booster Studio in Portal Data"
        >
          <Star size={12} className="text-amber-400 fill-amber-400 animate-pulse" />
          <span>REVIEW STUDIO</span>
          <ArrowRight size={10} className="text-amber-400" />
        </button>
      </div>

      {/* ── Input Handle: Downstream Action Connection from Luna Core Hub ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-review"
        className="!w-4 !h-4 !bg-purple-500 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-30 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Luna Core Action Ingest: in-review"
      />

      {/* ── Header ── */}
      <div className="p-4 border-b border-amber-500/30 bg-gradient-to-r from-amber-950/80 via-yellow-950/50 to-slate-950/80 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center text-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.25)]">
            <Star size={18} className="fill-amber-400/20 text-amber-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-slate-100 tracking-wide">
                Google Review Booster
              </h3>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                SEO & REVIEWS
              </span>
            </div>
            <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
              <span>Autonomous Google Maps rating & review generator</span>
            </p>
          </div>
        </div>

        {/* Status & Toggle */}
        <div className="flex items-center gap-2">
          {saveIndicator && (
            <span className="text-[9px] font-mono text-emerald-400 animate-pulse">
              {saveIndicator}
            </span>
          )}

          <button
            onClick={() => handleFieldChange('isEnabled', !isEnabled)}
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold font-mono transition-all border cursor-pointer ${
              isEnabled
                ? 'bg-amber-500/20 border-amber-500/40 text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.3)]'
                : 'bg-slate-800/60 border-slate-700 text-slate-500'
            }`}
          >
            {isEnabled ? 'ACTIVE' : 'MUTED'}
          </button>
        </div>
      </div>

      {/* ── Body Content ── */}
      <div className="p-4 space-y-3.5 text-xs">
        {/* Google Maps Review URL */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider flex items-center justify-between">
            <span className="flex items-center gap-1 text-amber-300">
              <ExternalLink size={11} /> Google Review Link
            </span>
            <span className="text-slate-500 font-normal">Google Maps Place URL</span>
          </label>
          <input
            type="text"
            value={googleReviewUrl}
            onChange={(e) => handleFieldChange('googleReviewUrl', e.target.value)}
            placeholder="https://g.page/r/your-place-id/review"
            className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-[11px] text-amber-200 placeholder-slate-600 focus:outline-none focus:border-amber-400 font-mono transition"
          />
        </div>

        {/* Trigger Delay After Delivery / Fulfilled */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Clock size={11} className="text-amber-400" /> Trigger Delay (Post-Fulfillment)
            </span>
            <span className="text-amber-400 font-bold font-mono">{delayHours} Hours</span>
          </label>
          <div className="grid grid-cols-4 gap-1.5">
            {[1, 2, 6, 24].map((hours) => (
              <button
                key={hours}
                type="button"
                onClick={() => handleFieldChange('delayHours', hours)}
                className={`py-1.5 px-2 rounded-lg text-center font-mono text-[11px] font-bold border transition-all cursor-pointer ${
                  delayHours === hours
                    ? 'bg-amber-500/25 border-amber-400 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.3)]'
                    : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                {hours === 24 ? '1 Day' : `${hours}h`}
              </button>
            ))}
          </div>
        </div>

        {/* Negative Feedback Firewall (Smart Sentinel) */}
        <div className="p-3 rounded-xl bg-slate-900/70 border border-amber-500/20 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldAlert size={14} className="text-amber-400" />
              <div>
                <h4 className="text-[11px] font-bold text-slate-200">Negative Feedback Firewall</h4>
                <p className="text-[10px] text-slate-400">Protect Google score from bad ratings</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={negativeFeedbackFirewall}
              onChange={(e) => handleFieldChange('negativeFeedbackFirewall', e.target.checked)}
              className="rounded bg-slate-800 border-slate-700 text-amber-500 focus:ring-0 cursor-pointer"
            />
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed pl-5">
            If the customer rates <strong>1-3 stars</strong> or complains, Luna intercepts the message and alerts the manager's WhatsApp immediately. Only satisfied customers (<strong>4-5 stars</strong>) receive the Google Maps review link.
          </p>
        </div>

        {/* Quiet Hours Protection */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900/40 border border-slate-800/80">
          <div className="flex items-center gap-2">
            <Moon size={12} className="text-indigo-400" />
            <span className="text-[11px] text-slate-300">Quiet Hours Guard (10 PM – 8 AM)</span>
          </div>
          <input
            type="checkbox"
            checked={quietHours}
            onChange={(e) => handleFieldChange('quietHours', e.target.checked)}
            className="rounded bg-slate-800 border-slate-700 text-indigo-500 focus:ring-0 cursor-pointer"
          />
        </div>

        {/* Advanced Settings Accordion */}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className="w-full flex items-center justify-between py-1 px-2 rounded bg-slate-900/40 hover:bg-slate-900/80 text-[10px] font-mono text-slate-400 transition cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <Sparkles size={11} className="text-amber-400" />
              Advanced Review Guidance & Reward Incentive
            </span>
            {isAdvancedOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>

          {isAdvancedOpen && (
            <div className="mt-2 space-y-2.5 p-3 rounded-xl bg-slate-900/90 border border-slate-800 text-slate-300">
              {/* Incentive Offer */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-mono font-bold text-slate-400 flex items-center gap-1">
                    <Gift size={11} className="text-emerald-400" /> Courtesy Review Incentive
                  </label>
                  <input
                    type="checkbox"
                    checked={includeIncentive}
                    onChange={(e) => handleFieldChange('includeIncentive', e.target.checked)}
                    className="rounded bg-slate-800 border-slate-700 text-emerald-500 focus:ring-0 cursor-pointer"
                  />
                </div>
                {includeIncentive && (
                  <input
                    type="text"
                    value={incentiveText}
                    onChange={(e) => handleFieldChange('incentiveText', e.target.value)}
                    placeholder="e.g. 5% discount on your next meal / purchase"
                    className="w-full px-2 py-1 rounded bg-slate-950 border border-slate-800 text-[11px] text-emerald-300 placeholder-slate-600 focus:outline-none focus:border-emerald-400 font-sans"
                  />
                )}
              </div>

              {/* Custom Guidance */}
              <div className="space-y-1">
                <label className="text-[10px] font-mono font-bold text-slate-400">
                  Custom Tone & Guidance (Luna AI)
                </label>
                <textarea
                  rows={2}
                  value={customGuidance}
                  onChange={(e) => handleFieldChange('customGuidance', e.target.value)}
                  placeholder="e.g. Always thank the customer warmly in Sinhala/English, asking if their meal or product met their expectations."
                  className="w-full p-2 rounded-lg bg-slate-950 border border-slate-800 text-[11px] text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-400 font-sans transition"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="px-4 py-2.5 bg-slate-950/80 border-t border-amber-500/10 rounded-b-2xl flex items-center justify-between text-[10px] font-mono text-slate-500">
        <span className="flex items-center gap-1 text-slate-400">
          <Sparkles size={11} className="text-amber-400" />
          Autonomous Rating Booster
        </span>
        <span className="text-amber-300 font-bold">
          {delayHours}h Post-Order • {isEnabled ? 'Armed' : 'Muted'}
        </span>
      </div>
    </div>
  );
};

export default memo(ReviewBoosterNode);
